import { MONTH_LONG, WEEKDAY_SHORT, addDays, parseDate, startOfWeek } from './dates';
import { compareText } from './members';
import { eventColor } from './palette';
import type { ScheduleEvent } from './types';

/**
 * O calendário exportado (Calendário › Exportar › modo Calendário): a grade do mês ou da
 * semana com os eventos de cada dia — título, horário e a cor da série —, sem nada de
 * escala (quem serve, vagas, situação). Serve para divulgar a agenda da igreja.
 */

export type CalendarExportKind = 'month' | 'week';

export interface CalendarExportEvent {
  id: string;
  title: string;
  time: string;
  /** A cor da série (a mesma do Calendário) e o fundo claro dela. */
  color: string;
  tint: string;
}

export interface CalendarExportDay {
  date: string;
  /** O dia do mês. */
  day: number;
  /** Fora do mês exportado (os dias que completam a primeira e a última semana). */
  outside: boolean;
  events: CalendarExportEvent[];
}

export interface CalendarDocument {
  kind: CalendarExportKind;
  /** "Calendário de novembro de 2026"; na semana, "Calendário da semana". */
  title: string;
  /** "16 eventos"; na semana, o período ("1 a 7 de novembro de 2026"). */
  subtitle: string;
  /** "novembro de 2026", "1 a 7 de novembro de 2026". */
  period: string;
  weekdays: readonly string[];
  /** As semanas, de domingo a sábado. */
  weeks: CalendarExportDay[][];
  count: number;
}

function weekLabel(from: string, to: string): string {
  const a = parseDate(from);
  const b = parseDate(to);
  const end = `${b.getDate()} de ${MONTH_LONG[b.getMonth()]} de ${b.getFullYear()}`;

  if (a.getMonth() === b.getMonth()) return `${a.getDate()} a ${end}`;

  const start = `${a.getDate()} de ${MONTH_LONG[a.getMonth()]}`;

  return a.getFullYear() === b.getFullYear()
    ? `${start} a ${end}`
    : `${start} de ${a.getFullYear()} a ${end}`;
}

/**
 * Monta o calendário de `events` no período: no mês, as semanas que cobrem do dia 1 ao
 * último (os dias de fora ficam vazios e marcados); na semana, os 7 dias a partir do
 * domingo de `from`. Os eventos fora do período não entram; os do dia, pelo horário.
 */
export function calendarDocument(
  events: readonly ScheduleEvent[],
  range: { from: string; to: string },
  kind: CalendarExportKind
): CalendarDocument {
  const first = startOfWeek(range.from);
  const last = kind === 'week' ? addDays(first, 6) : addDays(startOfWeek(range.to), 6);
  const inRange = events
    .filter((e) => e.date >= range.from && e.date <= range.to)
    .sort(
      (a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time) || compareText(a.title, b.title)
    );
  const byDate = new Map<string, CalendarExportEvent[]>();

  for (const e of inRange) {
    const { color, tint } = eventColor(e);

    byDate.set(e.date, [
      ...(byDate.get(e.date) ?? []),
      { id: e.id, title: e.title.trim(), time: e.time, color, tint },
    ]);
  }

  const weeks: CalendarExportDay[][] = [];

  for (let start = first; start <= last; start = addDays(start, 7)) {
    weeks.push(
      Array.from({ length: 7 }, (_, i) => {
        const date = addDays(start, i);

        return {
          date,
          day: parseDate(date).getDate(),
          outside: date < range.from || date > range.to,
          events: byDate.get(date) ?? [],
        };
      })
    );
  }

  const month = parseDate(range.from);
  const period =
    kind === 'week' ? weekLabel(first, last) : `${MONTH_LONG[month.getMonth()]} de ${month.getFullYear()}`;
  const count = `${inRange.length} ${inRange.length === 1 ? 'evento' : 'eventos'}`;

  return {
    kind,
    title: kind === 'week' ? 'Calendário da semana' : `Calendário de ${period}`,
    subtitle: kind === 'week' ? `${period} · ${count}` : count,
    period,
    weekdays: WEEKDAY_SHORT,
    weeks,
    count: inRange.length,
  };
}

/** Nome do arquivo: "calendario-novembro-de-2026". */
export function calendarFileName(doc: CalendarDocument): string {
  return `calendario ${doc.period}`
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
